import {
  Address,
  AssetKey,
  assetKeys,
  ConfigAssetPoints,
  DEFAULT_DEPOSIT_MODE,
  DepositMode,
  MarketConfig,
  PointsItem,
  positionTypes,
  ProtocolConfig,
  ProtocolKey,
  WarningConfig,
} from "@silo/types";
import { PROTOCOL_CONFIG } from ".";
import { Option, pipe } from "effect";
import { isNotNullable, isNullable } from "effect/Predicate";
import { Schema } from "@effect/schema";

export function getProtocolConfig(protocolKey: ProtocolKey): ProtocolConfig {
  return PROTOCOL_CONFIG[protocolKey];
}

export function getMarketConfig(
  protocolKey: ProtocolKey,
  marketAddress: Address
): Option.Option<MarketConfig> {
  return pipe(
    getProtocolConfig(protocolKey),
    (config) => config.markets[marketAddress],
    Option.fromNullable
  );
}

interface GetCurveLpMetaParams {
  protocolKey: ProtocolKey;
  marketAddress: Address;
  assetKey: AssetKey;
}

type CurveLpMeta =
  | {
      isCurveLpAsset: true;
      curveLpTokenAddress: Address;
    }
  | {
      isCurveLpAsset: false;
      curveLpTokenAddress: null;
    };

export function getCurveLpMeta({
  protocolKey,
  marketAddress,
  assetKey,
}: GetCurveLpMetaParams): CurveLpMeta {
  const curveLpTokenAddress = pipe(
    getMarketConfig(protocolKey, marketAddress),
    Option.flatMapNullable((config) => config.curveLpTokenAddress),
    Option.getOrNull
  );

  if (isNotNullable(curveLpTokenAddress) && assetKey === "base") {
    return {
      isCurveLpAsset: true,
      curveLpTokenAddress,
    };
  }

  return {
    isCurveLpAsset: false,
    curveLpTokenAddress: null,
  };
}

interface GetConfigDepositModeParams {
  protocolKey: ProtocolKey;
  marketAddress: Address;
  assetKey: AssetKey;
}

export function getConfigDepositMode({
  protocolKey,
  marketAddress,
  assetKey,
}: GetConfigDepositModeParams): DepositMode {
  return pipe(
    getMarketConfig(protocolKey, marketAddress),
    Option.flatMapNullable((config) => config.depositMode),
    Option.flatMapNullable((depositMode) => depositMode[assetKey]),
    Option.getOrElse(() => DEFAULT_DEPOSIT_MODE)
  );
}

interface GetConfigIsDebtAprFixedParams {
  protocolKey: ProtocolKey;
  marketAddress: Address;
  assetKey: AssetKey;
}

export function getConfigIsDebtAprFixed({
  protocolKey,
  marketAddress,
  assetKey,
}: GetConfigIsDebtAprFixedParams): boolean {
  return pipe(
    getMarketConfig(protocolKey, marketAddress),
    Option.flatMapNullable((config) => config.isDebtAprFixed),
    Option.flatMapNullable((isDebtAprFixed) => isDebtAprFixed[assetKey]),
    Option.getOrElse(() => false)
  );
}

const emptyPoints: readonly PointsItem[] = [];

interface GetConfigPointsByTypeParams {
  protocolKey: ProtocolKey;
  marketAddress: Address;
  assetKey: AssetKey;
}

export function getConfigPointsByType({
  protocolKey,
  marketAddress,
  assetKey,
}: GetConfigPointsByTypeParams) {
  const marketConfig = getMarketConfig(protocolKey, marketAddress);

  const pointsConfig = pipe(
    marketConfig,
    Option.flatMapNullable((config) => config.points),
    Option.flatMapNullable((points) => points[assetKey])
  );

  const collateralPoints = pipe(
    pointsConfig,
    Option.flatMapNullable((config) => config.collateral),
    Option.getOrElse(() => emptyPoints)
  );

  const protectedPoints = pipe(
    pointsConfig,
    Option.flatMapNullable((config) => config.protected),
    Option.getOrElse(() => emptyPoints)
  );

  const debtPoints = pipe(
    pointsConfig,
    Option.flatMapNullable((config) => config.debt),
    Option.getOrElse(() => emptyPoints)
  );

  return {
    collateralPoints,
    protectedPoints,
    debtPoints,
  };
}

interface GetConfigAssetPointsParams {
  protocolKey: ProtocolKey;
  marketAddress: Address;
  assetKey: AssetKey;
}

export function getConfigAssetPoints({
  protocolKey,
  marketAddress,
  assetKey,
}: GetConfigAssetPointsParams): ConfigAssetPoints {
  return pipe(
    getMarketConfig(protocolKey, marketAddress),
    Option.flatMapNullable((config) => config.points),
    Option.flatMapNullable((points) => points[assetKey]),
    Option.getOrElse((): ConfigAssetPoints => ({}))
  );
}

interface GetHasConfigPointsParams {
  protocolKey: ProtocolKey;
  marketAddress: Address;
}

export function getHasConfigPoints({
  protocolKey,
  marketAddress,
}: GetHasConfigPointsParams): boolean {
  const points = pipe(
    getMarketConfig(protocolKey, marketAddress),
    Option.flatMapNullable((config) => config.points),
    Option.getOrNull
  );

  if (isNullable(points)) return false;

  const allPoints: PointsItem[] = [];

  for (const assetKey of assetKeys) {
    const configAssetPoints = points[assetKey];

    if (configAssetPoints) {
      for (const type of positionTypes) {
        const typePoints = configAssetPoints[type];

        if (typePoints) {
          allPoints.push(...typePoints);
        }
      }
    }
  }

  return allPoints.length > 0;
}

interface GetWarningConfigParams {
  protocolKey: ProtocolKey;
  marketAddress: Address;
}

export function getWarningConfig({
  protocolKey,
  marketAddress,
}: GetWarningConfigParams): WarningConfig | null {
  const warningConfig = pipe(
    getMarketConfig(protocolKey, marketAddress),
    Option.flatMapNullable((config) => config.warningConfig),
    Option.getOrNull
  );

  return warningConfig;
}

interface GetHasUnstableOracleParams {
  protocolKey: ProtocolKey;
  marketAddress: Address;
}

export function getHasUnstableOracle({
  protocolKey,
  marketAddress,
}: GetHasUnstableOracleParams): boolean {
  return pipe(
    getMarketConfig(protocolKey, marketAddress),
    Option.flatMapNullable((config) => config.hasUnstableOracle),
    Option.getOrElse(() => false)
  );
}

interface GetConfigIsDeprecatedParams {
  protocolKey: ProtocolKey;
  marketAddress: Address;
}

export function getConfigIsDeprecated({
  protocolKey,
  marketAddress,
}: GetConfigIsDeprecatedParams): boolean {
  return pipe(
    getMarketConfig(protocolKey, marketAddress),
    Option.flatMapNullable((config) => config.labelAsDeprecated),
    Option.getOrElse(() => false)
  );
}

export function getConfigSuppressedTvlMarkets(
  protocolKey: ProtocolKey
): Set<Address> {
  const protocolConfig = getProtocolConfig(protocolKey);
  const set = new Set<Address>();

  for (const [marketAddress, config] of Object.entries(
    protocolConfig.markets
  )) {
    if (Schema.is(Address)(marketAddress) && config?.suppressTvl) {
      set.add(marketAddress);
    }
  }

  return set;
}
